import { Box, Button, Text } from '@user-portal/wikr-components';
import { trackLoginPageLoad, trackLoginSubmit } from 'analytics/trackers/login';
import * as S from 'containers/AuthFlow/styled';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { signInRequest } from 'store/auth/actions';

import { useDispatch, useScreenLoadTracker } from 'hooks';

import { isEmpty } from 'helpers/utils';

import FormikInput from 'components/FormikInput';

import man from 'assets/img/webp/login-man.webp';
import text from 'assets/img/webp/login-text.webp';

import { CONTENT } from './config';
import { LoginSchema } from './validationSchema';

type FormValue = {
    email: string;
    password: string;
};

export const Login = () => {
    useScreenLoadTracker(trackLoginPageLoad);
    const { t } = useTranslation();
    const [isFetching, setFetchingStatus] = useState(false);

    const dispatch = useDispatch();
    const { email } = (useLocation().state as { email?: string }) || {};

    const {
        errors,
        dirty,
        values,
        initialValues,
        setFieldTouched,
        setFieldValue,
        touched,
        handleSubmit,
        setFieldError,
    } = useFormik({
        initialValues: {
            email: email || '',
            password: '',
        },
        validationSchema: LoginSchema,
        validateOnBlur: true,
        onSubmit: (userData: FormValue) => {
            trackLoginSubmit();
            setFetchingStatus(true);
            dispatch(
                signInRequest({
                    userData,
                    onError: handleLoginError,
                })
            );
        },
    });

    const handleLoginError = () => {
        CONTENT.forEach((field) => setFieldError(field.name, t('login.error')));
        setFetchingStatus(false);
    };

    return (
        <S.AuthContainer>
            <S.AuthImage>
                <S.LoginText src={text} alt="changeYourself" />
                <S.LoginMan src={man} alt="man" />
            </S.AuthImage>
            <S.AuthForm>
                <Box $paddingBottom={32} $paddingX={16}>
                    <form onSubmit={handleSubmit}>
                        <Text type="h5" bold center mb={12}>
                            {t('login.title')}
                        </Text>
                        <Box $mb={44}>
                            {CONTENT.map(({ name, label, type, icon, dataLocator, placeholder }) => (
                                <FormikInput
                                    key={name}
                                    mb={32}
                                    type={type}
                                    field={name}
                                    label={label}
                                    setFieldTouched={setFieldTouched}
                                    initialValues={initialValues}
                                    setFieldValue={setFieldValue}
                                    touched={touched}
                                    values={values}
                                    errors={errors}
                                    leftIcon={icon}
                                    dataLocator={dataLocator}
                                    placeholder={placeholder}
                                />
                            ))}
                        </Box>
                        <Button
                            onClick={handleSubmit}
                            disabled={!dirty || !isEmpty(errors) || isFetching}
                            mb={24}
                            isLoading={isFetching}
                            dataLocator="loginButton"
                        >
                            {t('basics.login')}
                        </Button>
                    </form>
                </Box>
            </S.AuthForm>
        </S.AuthContainer>
    );
};
